<template>
	<PageWrapper>
		<HeroesHome />

		<VectorsMobileFiber />
		<VectorsAnimatedFiber />

		<SectionContentContainer
			classes="-mt-16 pb-12 lg:pb-16 min-h-[500px] contain-layout"
		>
			<template #items>
				<LazyVisibleSectionContentItem
					v-for="(item, index) in $tm('home.section-items')"
					:key="item"
					:copy="item"
					:icon="HOME_SECTION_ITEMS[index]?.icon"
					:viewbox="HOME_SECTION_ITEMS[index]?.viewbox"
					:image="HOME_SECTION_ITEMS[index]?.image"
					:lines="HOME_SECTION_ITEMS[index]?.lines"
					:circles="HOME_SECTION_ITEMS[index]?.circles"
					hydrate-on-visible
					:lazy="index > 2"
					class="min-h-[200px] contain-layout"
				/>
			</template>
		</SectionContentContainer>

		<div class="contain-layout">
			<LazyVisibleTestimonials
				hydrate-on-visible
				class="contain-layout"
			/>
			<LazyVisibleFaqs
				hydrate-on-visible
				:faqs="faqs"
				divider="both"
				class="contain-layout"
			/>
			<LazyVisibleBlogCarousel
				id="home-page"
				hydrate-on-visible
				class="contain-layout"
			/>
			<LazyVisibleBannersAddressChecker
				hydrate-on-visible
				class="contain-layout"
			/>
		</div>
	</PageWrapper>
</template>

<script setup lang="ts">
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'
import { HOME_SECTION_ITEMS } from '@/constants/components/home-sections'
import {
	BASE_URL,
	COMPANY_NAME
} from '@/constants/index.js'
import {
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_LOGO,
	JSON_LD_ORGANIZATION_ADDRESS,
	JSON_LD_ORGANIZATION_AREA_SERVED,
	JSON_LD_ORGANIZATION_CONTACT_POINT,
	JSON_LD_ORGANIZATION_SAME_AS
} from '@/constants/json-ld'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({
	useScope: 'global'
})

const { getStory } = useStoryblokWithErrorReporting()

const storyblok = await getStory('home')

const { story } = storyblok.value?.data || {}

const faqs = computed(() => {
	return story?.content?.faqContent
})

useHead({
	title: t('meta-title'),
	meta: [
		{ name: 'description',
			content: t('meta-description', { COMPANY_NAME }) },
		{ name: 'url',
			content: BASE_URL }
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})

const {
	title = '',
	description = ''
} = story?.content?.metatags || {}

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogImage: () => '/images/logo-full.svg',
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/logo-full.svg',
	twitterCard: () => 'summary_large_image'
})

useJsonld({
	'@context': 'https://schema.org',
	'@type': 'Organization',
	'name': COMPANY_NAME,
	'url': BASE_URL,
	'logo': JSON_LD_LOGO,
	'description': t('meta-description', { COMPANY_NAME }),
	'contactPoint': JSON_LD_ORGANIZATION_CONTACT_POINT,
	'sameAs': JSON_LD_ORGANIZATION_SAME_AS,
	'address': JSON_LD_ORGANIZATION_ADDRESS,
	'areaServed': JSON_LD_ORGANIZATION_AREA_SERVED,
	'hasOfferCatalog': JSON_LD_HAS_OFFER_CATALOG
})
</script>
